import { Col, Divider, Radio, Row, Space } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomInput from '../../../components/CustomInput';
import CustomSelect from '../../../components/CustomSelect';
import LocalLoader from '../../../components/LocalLoader';
import MultinationalTabs from '../../../components/MultinationalTabs';
import Title from '../../../components/Title';
import {
    createPackage,
    editPackage,
    getPackageById,
    onePackage,
    resetPackage,
} from '../../../redux/slices/insuranceSlice';
import AssistanceAndInsuranceEditors from './AssistanceAndInsuranceEditors';
import { createShallowCopy } from '../../../utils/helpFunctions';

const providers = [
    { id: 'mysafety', value: 'mysafety', label: 'MySafety' },
    { id: 'tryg', value: 'tryg', label: 'Tryg' },
];

const packageType = [
    { id: 'SINGLE', value: 'single', label: 'SINGLE' },
    { id: 'FAMILY', value: 'family', label: 'FAMILY' },
    { id: 'BUSINESS', value: 'business', label: 'BUSINESS' },
];

const countryCode = [
    { id: 'SE', value: 'sweden', label: 'Sweden' },
    { id: 'FI', value: 'finland', label: 'Finland' },
    { id: 'NO', value: 'norway', label: 'Norway' },
    { id: 'DK', value: 'denmark', label: 'Denmark' },
    { id: 'IS', value: 'iceland', label: 'Iceland' },
];

export default function CreatePackagePage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const params = useParams();

    const insurancePackage = useSelector(onePackage);

    const countriesLocalizationsData = useMemo(
        () =>
            insurancePackage?.details?.reduce((acc, el) => {
                acc[el.lang_code] = el;
                return acc;
            }, {}),
        [insurancePackage],
    );

    const [isStateLoaded, setIsStateLoaded] = useState(false);
    const [radioButtonValue, setRadioButtonValue] = useState(false);

    const [data, setData] = useState({
        name: '',
        event_name: '',
        purchase_price: '',
        insurance_premium_id: '',
        service_provider: '',
        is_integration: '',
        type: '',
        country_code: '',
        is_plan_attached: false,
        details: {
            sv: {
                personal_assistance_block: '',
                company_assistance_block: '',
                personal_insurance_block: '',
                company_insurance_block: '',
                info_boxes: [],
            },
            no: {
                personal_assistance_block: '',
                company_assistance_block: '',
                personal_insurance_block: '',
                company_insurance_block: '',
                info_boxes: [],
            },
            fi: {
                personal_assistance_block: '',
                company_assistance_block: '',
                personal_insurance_block: '',
                company_insurance_block: '',
                info_boxes: [],
            },
            da: {
                personal_assistance_block: '',
                company_assistance_block: '',
                personal_insurance_block: '',
                company_insurance_block: '',
                info_boxes: [],
            },
            en: {
                personal_assistance_block: '',
                company_assistance_block: '',
                personal_insurance_block: '',
                company_insurance_block: '',
                info_boxes: [],
            },
            is: {
                personal_assistance_block: '',
                company_assistance_block: '',
                personal_insurance_block: '',
                company_insurance_block: '',
                info_boxes: [],
            },
        },
    });

    const [action, setAction] = useState('create');
    const [errors, setErrors] = useState({});
    const [serviceType, setServiceType] = useState(['']);

    useEffect(() => {
        if (location?.pathname?.includes('edit')) {
            setAction('edit');
            dispatch(getPackageById(params?.id));
        }
    }, [dispatch, location, params]);

    useEffect(() => {
        setData({
            name: insurancePackage?.name || '',
            event_name: insurancePackage?.event_name || '',
            purchase_price: insurancePackage?.purchase_price || '',
            insurance_premium_id: insurancePackage?.insurance_premium_id || '',
            service_provider: insurancePackage?.service_provider || 'mysafety',
            is_integration: insurancePackage?.is_integration || false,
            type: insurancePackage?.type || 'SINGLE',
            country_code: insurancePackage?.country_code || 'SE',
            is_plan_attached: insurancePackage?.is_plan_attached || false,
            details: {
                sv: {
                    personal_assistance_block:
                        countriesLocalizationsData?.sv
                            ?.personal_assistance_block || '',
                    company_assistance_block:
                        countriesLocalizationsData?.sv
                            ?.company_assistance_block || '',
                    personal_insurance_block:
                        countriesLocalizationsData?.sv
                            ?.personal_insurance_block || '',
                    company_insurance_block:
                        countriesLocalizationsData?.sv
                            ?.company_insurance_block || '',
                    info_boxes:
                        countriesLocalizationsData?.sv?.info_boxes || [],
                },
                no: {
                    personal_assistance_block:
                        countriesLocalizationsData?.no
                            ?.personal_assistance_block || '',
                    company_assistance_block:
                        countriesLocalizationsData?.no
                            ?.company_assistance_block || '',
                    personal_insurance_block:
                        countriesLocalizationsData?.no
                            ?.personal_insurance_block || '',
                    company_insurance_block:
                        countriesLocalizationsData?.no
                            ?.company_insurance_block || '',
                    info_boxes:
                        countriesLocalizationsData?.no?.info_boxes || [],
                },
                fi: {
                    personal_assistance_block:
                        countriesLocalizationsData?.fi
                            ?.personal_assistance_block || '',
                    company_assistance_block:
                        countriesLocalizationsData?.fi
                            ?.company_assistance_block || '',
                    personal_insurance_block:
                        countriesLocalizationsData?.fi
                            ?.personal_insurance_block || '',
                    company_insurance_block:
                        countriesLocalizationsData?.fi
                            ?.company_insurance_block || '',
                    info_boxes:
                        countriesLocalizationsData?.fi?.info_boxes || [],
                },
                da: {
                    personal_assistance_block:
                        countriesLocalizationsData?.da
                            ?.personal_assistance_block || '',
                    company_assistance_block:
                        countriesLocalizationsData?.da
                            ?.company_assistance_block || '',
                    personal_insurance_block:
                        countriesLocalizationsData?.da
                            ?.personal_insurance_block || '',
                    company_insurance_block:
                        countriesLocalizationsData?.da
                            ?.company_insurance_block || '',
                    info_boxes:
                        countriesLocalizationsData?.da?.info_boxes || [],
                },
                en: {
                    personal_assistance_block:
                        countriesLocalizationsData?.en
                            ?.personal_assistance_block || '',
                    company_assistance_block:
                        countriesLocalizationsData?.en
                            ?.company_assistance_block || '',
                    personal_insurance_block:
                        countriesLocalizationsData?.en
                            ?.personal_insurance_block || '',
                    company_insurance_block:
                        countriesLocalizationsData?.en
                            ?.company_insurance_block || '',
                    info_boxes:
                        countriesLocalizationsData?.en?.info_boxes || [],
                },
                is: {
                    personal_assistance_block:
                        countriesLocalizationsData?.is
                            ?.personal_assistance_block || '',
                    company_assistance_block:
                        countriesLocalizationsData?.is
                            ?.company_assistance_block || '',
                    personal_insurance_block:
                        countriesLocalizationsData?.is
                            ?.personal_insurance_block || '',
                    company_insurance_block:
                        countriesLocalizationsData?.is
                            ?.company_insurance_block || '',
                    info_boxes:
                        countriesLocalizationsData?.is?.info_boxes || [],
                },
            },
        });
        setServiceType(
            insurancePackage?.service_types?.length
                ? insurancePackage.service_types.map(
                      (type) => type.service_type,
                  )
                : [''],
        );
        setRadioButtonValue(insurancePackage?.is_integration || false);

        if (location?.pathname?.includes('create') || insurancePackage?.name) {
            setIsStateLoaded(true);
        }
    }, [countriesLocalizationsData, insurancePackage, location]);

    useEffect(() => {
        return () => dispatch(resetPackage());
    }, [dispatch]);

    const onInputChange = (e) => {
        const value = e.target.value;
        setData({
            ...data,
            [e.target.id]: value,
        });
    };

    const onSelectChange = (option, selectName) => {
        setData({ ...data, [selectName]: option.id });
    };

    const onRadioButtonsChange = (e) => {
        setRadioButtonValue(e.target.value);
        setData({ ...data, is_integration: e.target.value });
    };

    const onChangeMultinational = (text, flag, id) => {
        const dataCopy = createShallowCopy(data);
        const currentEditor = id.slice(0, id.length - 3);
        dataCopy.details[flag][currentEditor] = text;

        setData(dataCopy);
    };

    const onServiceTypeChange = (e, index) => {
        let newServiceType = [...serviceType];
        newServiceType[index] = e.target.value;

        setServiceType(newServiceType);
    };

    const onAddDeleteServiceType = (index, action) => {
        delete errors[`service_types.0`];
        delete errors[`service_types.1`];
        let newServiceType = [...serviceType];

        if (action === 'add') newServiceType.push('');
        else newServiceType.splice(index, 1);

        setServiceType(newServiceType);
    };

    const createRequestDetailsData = (data) =>
        Object.entries(data.details).map((el) => ({
            lang_code: el[0],
            ...el[1],
        }));

    const onCreatePackage = async (e) => {
        e.preventDefault();

        const requestDetailsData = createRequestDetailsData(data);
        const request =
            data.type === 'BUSINESS'
                ? {
                      ...data,
                      details: requestDetailsData,
                      service_types: serviceType,
                  }
                : { ...data, details: requestDetailsData };

        try {
            await dispatch(createPackage(request)).unwrap();
            toast.success('New package successfully created');
            navigate('../insurance');
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onUpdatePackage = async (e) => {
        e.preventDefault();

        const requestDetailsData = createRequestDetailsData(data);
        let request =
            data.type === 'BUSINESS'
                ? {
                      ...data,
                      details: requestDetailsData,
                      service_types: serviceType,
                  }
                : { ...data, details: requestDetailsData };
        request = data.is_plan_attached
            ? { name: data.name, details: requestDetailsData }
            : { ...request };

        try {
            await dispatch(
                editPackage({ body: request, id: params.id }),
            ).unwrap();
            toast.success('Changes saved successfully');
            navigate('../insurance');
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onCancelButtonClick = (e) => {
        e.preventDefault();
        navigate(-1);
    };

    const onAddInfoBox = (infoBoxType, flag) => {
        const dataCopy = createShallowCopy(data);

        dataCopy.details[flag].info_boxes.push({
            block_type: `${infoBoxType}_block`,
            block_content: '',
        });

        setData(dataCopy);
    };

    const onRemoveInfoBox = (flag, id) => {
        const dataCopy = createShallowCopy(data);

        const filteredInfoBoxes = dataCopy.details[flag].info_boxes.filter(
            (el) => el.id !== id,
        );
        dataCopy.details[flag].info_boxes = filteredInfoBoxes;

        setData(dataCopy);
    };

    const onChangeInfoBox = (text, flag, id) => {
        const dataCopy = createShallowCopy(data);
        const infoBoxIndex = dataCopy.details[flag].info_boxes.findIndex(
            (el) => el.id === id,
        );
        const infoBox = dataCopy.details[flag].info_boxes[infoBoxIndex];

        infoBox.block_content = text;

        setData(dataCopy);
    };

    return (
        <>
            <Title subText="Please fill information about package below">
                {action === 'edit' ? 'Update package' : 'Add new package'}
            </Title>
            <form>
                <Row>
                    <Col span={10}>
                        <p className="form__input-group-header">GENERAL</p>
                        <div className="form__item-wrapper">
                            <CustomInput
                                id="name"
                                placeholder="Enter package name"
                                onInputChange={onInputChange}
                                inputValue={data.name}
                                labelText="Package name"
                                width="100%"
                                errorText={errors?.name}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Package type</p>
                            <CustomSelect
                                name="type"
                                onChange={(option) =>
                                    onSelectChange(option, 'type')
                                }
                                options={packageType}
                                placeholder="Select package type"
                                value={data.type}
                                errorText={errors?.type}
                                isDisabled={data.is_plan_attached}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Integration</p>
                            <div>
                                <Radio.Group
                                    onChange={onRadioButtonsChange}
                                    value={radioButtonValue}
                                    disabled={data.is_plan_attached}
                                >
                                    <Space direction="horizontal">
                                        <Radio value={true}>Yes</Radio>
                                        <Radio value={false}>No</Radio>
                                    </Space>
                                </Radio.Group>
                            </div>
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Providers</p>
                            <CustomSelect
                                name="service_provider"
                                onChange={(option) =>
                                    onSelectChange(option, 'service_provider')
                                }
                                options={providers}
                                placeholder="Select provider"
                                value={data.service_provider}
                                errorText={errors?.service_provider}
                                isDisabled={data.is_plan_attached}
                            />
                        </div>
                        {radioButtonValue && (
                            <>
                                <div className="form__item-wrapper">
                                    <CustomInput
                                        id="event_name"
                                        placeholder="Enter event name"
                                        onInputChange={onInputChange}
                                        inputValue={data.event_name}
                                        labelText="Event Name"
                                        width="100%"
                                        errorText={errors?.event_name}
                                        isDisabled={data.is_plan_attached}
                                    />
                                </div>
                                <div className="form__item-wrapper">
                                    <CustomInput
                                        id="purchase_price"
                                        placeholder="Enter purchase price"
                                        onInputChange={onInputChange}
                                        inputValue={data.purchase_price}
                                        labelText="Purchase Price In Cents"
                                        width="100%"
                                        errorText={errors?.purchase_price}
                                        isDisabled={data.is_plan_attached}
                                    />
                                </div>
                                <div className="form__item-wrapper">
                                    <CustomInput
                                        id="insurance_premium_id"
                                        placeholder="Enter premium id"
                                        onInputChange={onInputChange}
                                        inputValue={data.insurance_premium_id}
                                        labelText="Insurance Premium Id (Contract ID)"
                                        width="100%"
                                        errorText={errors?.insurance_premium_id}
                                        isDisabled={data.is_plan_attached}
                                    />
                                </div>
                            </>
                        )}
                        {data.type === 'BUSINESS' &&
                            serviceType.map((type, index) => {
                                return (
                                    <div
                                        key={index}
                                        className="form__item-wrapper"
                                    >
                                        <CustomInput
                                            id={`service_type_${index + 1}`}
                                            placeholder="Enter service type"
                                            onInputChange={(e) =>
                                                onServiceTypeChange(e, index)
                                            }
                                            inputValue={type}
                                            labelText={
                                                index === 0
                                                    ? 'Service type'
                                                    : null
                                            }
                                            width="100%"
                                            errorText={
                                                errors?.[
                                                    `service_types.${index}`
                                                ]
                                            }
                                            customEl={
                                                data.is_plan_attached ? null : index ===
                                                  0 ? (
                                                    <PlusOutlined
                                                        onClick={() =>
                                                            onAddDeleteServiceType(
                                                                index,
                                                                'add',
                                                            )
                                                        }
                                                        style={{
                                                            marginLeft: 10,
                                                        }}
                                                    />
                                                ) : (
                                                    <MinusOutlined
                                                        onClick={() =>
                                                            onAddDeleteServiceType(
                                                                index,
                                                                'delete',
                                                            )
                                                        }
                                                        style={{
                                                            marginLeft: 10,
                                                        }}
                                                    />
                                                )
                                            }
                                            isDisabled={data.is_plan_attached}
                                        />
                                    </div>
                                );
                            })}
                        <div className="form__item-wrapper">
                            <p className="input-label">Country code</p>
                            <CustomSelect
                                name="country_code"
                                onChange={(option) =>
                                    onSelectChange(option, 'country_code')
                                }
                                options={countryCode}
                                placeholder="Select country code"
                                value={data.country_code}
                                errorText={errors?.country_code}
                                isDisabled={data.is_plan_attached}
                            />
                        </div>
                        <Divider />
                    </Col>
                </Row>
                <Row>
                    <Col span={20}>
                        <p className="form__input-group-header">
                            ASSISTANCE AND INSURANCE
                        </p>
                        <LocalLoader isLoading={!isStateLoaded}>
                            <MultinationalTabs>
                                <AssistanceAndInsuranceEditors
                                    data={data.details}
                                    onChange={onChangeMultinational}
                                    onAddInfoBox={onAddInfoBox}
                                    onRemoveInfoBox={onRemoveInfoBox}
                                    onChangeInfoBox={onChangeInfoBox}
                                />
                            </MultinationalTabs>
                        </LocalLoader>
                    </Col>
                </Row>
                <Row>
                    <Col span={10}>
                        <div className="form__footer">
                            <button
                                className="modal__button modal__button-cancel"
                                onClick={onCancelButtonClick}
                            >
                                Cancel
                            </button>
                            <button
                                className="modal__button modal__button-accept"
                                onClick={
                                    action === 'edit'
                                        ? onUpdatePackage
                                        : onCreatePackage
                                }
                            >
                                {action === 'edit' ? 'Update' : 'Add'}
                            </button>
                        </div>
                    </Col>
                </Row>
            </form>
        </>
    );
}
